<template>
  <div class="bg-gray-950 border-b border-gray-700 select-none">
    <div class="hidden md:flex md:items-center h-16">
      <div class="flex ml-12 w-full gap-2" v-if="canStartServer()">
        <div class="flex w-full justify-end items-center gap-2" v-if="!activeServer?.id">
          <LanOnPill compact />
          <QueueSearchPill compact />
          <router-link to="/play" class="text-[12px] px-4 font-medium flex cursor-pointer items-center justify-center gap-1.5 rounded-md bg-danger-700 duration-200 h-8 text-left text-text-primary hover:bg-danger-600 focus:outline-none focus:ring-0">
            {{ $t('layout.navbar.play_now') }} <img src="../../assets/logos/refrag-icon.svg" class="w-3 h-3" />
          </router-link>
        </div>
        <div
          v-else-if="activeServer?.status && activeServer?.status !== 'online'"
          class="w-full flex justify-end items-center gap-2"
        >
          <LanOnPill compact />
          <QueueSearchPill compact />
          <div
            class="inline-flex h-9 rounded-md justify-end items-center px-3 h-8 border select-none border-transparent text-sm font-medium text-white bg-danger-700 hover:bg-danger-600"
          >
            <svg class="w-6 h-6 text-white animate-spin" fill="none" viewBox="0 0 24 24">
              <circle
                class="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                stroke-width="4"
              />
              <path
                class="opacity-75"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                fill="currentColor"
              />
            </svg>
          </div>

          <div
            @click="toggleTerminate()"
            class="flex items-center justify-center bg-gray-800 cursor-pointer rounded-lg h-9 w-9 hover:bg-gray-700 duration-300"
          >
            <XMarkIcon class="w-5 h-5 text-text-secondary" @click="openServerSelector()" />
          </div>
        </div>
        <div class="flex items-center gap-2" v-else>
          <LanOnPill compact />
          <QueueSearchPill compact />
          <ServerInfo
            :ip="serverIP"
            :port="serverPort"
            :password="serverPassword"
            :id="serverID"
            :isAssessment="isAssessment"
            :raw_ip="serverRawIP"
            :streamerMode="user.streamer_mode"
          />
        </div>
        <div class="flex items-center" v-if="activeServers?.length && user.access_level > 2">
          <Popover v-slot="{ open }" as="div" class="relative inline-block text-left">
            <PopoverButton :class="open ? 'bg-gray-700' : 'bg-gray-800'" class="flex rounded-lg px-2.5 h-8 items-center justify-center gap-1 hover:bg-gray-700 duration-300">
              <img src="../../assets/icons/server.svg" class="w-3 h-3 brightness-200" />
              <h2 class="text-white text-sm font-medium">
                {{ activeServers?.length }}
              </h2>
              <ChevronDownIcon
                class="flex-shrink-0 h-3 w-3 text-text-primary group-hover:text-text-primary"
                aria-hidden="true"
              />
            </PopoverButton>
            <PopoverPanel class="absolute z-10 mt-2 right-0 origin-top-right w-80 rounded-md shadow-lg bg-gray-900 border border-gray-800">
              <div class="p-4 flex flex-col">
                <h3 class="text-text-primary font-medium text-lg">{{ $t('layout.navbar.team_servers') }}</h3>
                <ul class="gap-4 flex flex-col divide-y divide-gray-800">
                  <li v-for="server in activeServers" :key="server?.status" class="pt-4 text-text-secondary flex flex-col gap-2">
                    <div class="text-text-primary flex flex-row gap-2 items-center">
                      <img :src="server?.started_by?.steam_profile?.avatarfull" class="w-8 h-8 rounded-full" />
                      <p class="text-sm">{{ $t('layout.navbar.started_by', { name: server?.started_by?.steam_profile?.personaname }) }}</p>
                    </div>
                    <div
                      :class="[
                        'border bg-gray-800 p-2 rounded-md gap-2 relative text-sm focus:ring flex-row flex shadow-inner pl-3 h-full items-center',
                        state.copySucceeded === server.id ? 'border-success-600 duration-300' : 'duration-300 border-gray-700',
                      ]"
                    >
                      <p
                        class="flex text-xs font-mono items-center overflow-hidden whitespace-nowrap cursor-pointer text-ellipsis"
                        @click="copyToClipboard(server)"
                        :title="$t('layout.navbar.copy_connect_command')"
                      >
                        {{ `connect ${server.ip}:${server.port}; password `}}
                        <span
                          :class="{
                            'ml-1': true,
                            'blur-sm': user.streamer_mode,
                          }"
                        >{{ server.password }}</span>
                      </p>
                      <div class="flex align-center justify-center cursor-pointer">
                        <img
                          @click="copyToClipboard(server)"
                          :title="$t('layout.navbar.copy_connect_command')"
                          src="../../assets/icons/clipboard.svg"
                          v-if="state.copySucceeded !== server.id"
                          class="min-w-4 min-h-4 duration-300"
                        />
                        <CheckCircleIcon class="w-3 h-3 text-green-400 duration-300" v-else />
                      </div>
                      <a
                        class="flex align-center justify-center cursor-pointer"
                        :title="$t('layout.navbar.join_on_server', { name: server?.started_by?.steam_profile?.personaname })"
                        :href="`steam://connect/${server.raw_ip}:${server.port}/${server.password}`"
                        @click.stop>
                        <img src="../../assets/icons/play.svg" class="min-w-4 min-h-4" />
                      </a>
                    </div>
                  </li>
                </ul>
              </div>
            </PopoverPanel>
          </Popover>
        </div>
      </div>

      <div class="mt-4 flex flex-1 justify-end items-center gap-2 md:mt-0 md:ml-4" v-else>
        <LanOnPill compact />
        <QueueSearchPill compact />
        <div v-if="serverLauncherDisabled" class="has-tooltip flex flex-col">
          <button
            type="button"
            class="text-white bg-gray-300 font-medium text-sm px-5 h-8 text-center"
          >
            {{ $t('layout.navbar.under_maintenance') }}
          </button>
          <span
            href="https://discord.gg/Refrag"
            class="tooltip rounded shadow-md p-1 bg-gray-900 text-white mr-2 mt-16"
          >{{ $t('layout.navbar.servers_under_maintenance') }}</span
          >
        </div>
        <div v-else class="flex flex-col">
          <button
            @click="$emit('openUpgradeModal')"
            type="button"
            class="text-white bg-primary-600 hover:bg-primary-500 duration-300 font-medium text-sm px-5 h-8 text-center rounded-md"
          >
            {{ $t('layout.navbar.play_now') }}
          </button>
        </div>
      </div>
      <Streaks :user="user" />
      <div class="flex flex-shrink-0 items-center justify-end md:ml-4">
        <div class="relative flex flex-shrink-0 items-center">
          <img
            src="../../assets/icons/bell.svg"
            class="w-5 h-5 flex-shrink-0 cursor-pointer hover:scale-[1.1] transition-transform duration-300"
            @click="toggleNotifications"
          />
          <div
            v-if="state.notifications.length > 0"
            class="absolute -top-2 -right-2 bg-red-500 px-[4px] rounded-full text-white text-xs select-none pointer-events-none"
          >
            {{ state.notifications.length }}
          </div>
        </div>

        <div class="pr-8 flex items-center">
          <!-- Profile sidebar with party/friends management, for matchmaking users -->
          <template v-if="canUseProfileSidebar">
            <button
              @click="profileSidebarOpen = !profileSidebarOpen"
              class="group flex items-center gap-2 pl-4 relative z-40"
            >
              <span class="relative flex-shrink-0">
                <img
                  :class="[
                    'w-8 h-8 flex-shrink-0 object-cover rounded-full transition-transform duration-300 group-hover:scale-105',
                    impersonating ? 'border-2 border-orange-500' : null,
                  ]"
                  :src="imageURL" />
                <BetaBadge v-if="user.is_beta_tester" class="ring-gray-950" />
              </span>
              <img
                src="../../assets/icons/dropdown.svg"
                class="w-3 h-3 flex-shrink-0 transition-transform duration-300"
                :class="profileSidebarOpen ? 'rotate-180' : ''"
              />
            </button>
            <ProfileSidebar
              v-if="profileSidebarOpen || pinProfileSidebar"
              :open="profileSidebarOpen"
              :pinned="pinProfileSidebar"
              :user="user"
              :can-impersonate="canImpersonate"
              :impersonating="impersonating"
              @close="profileSidebarOpen = false"
              @open-upgrade-modal="$emit('openUpgradeModal')"
              @logout="logout"
              @impersonate="startImpersonating"
              @stop-impersonating="stopImpersonating"
            />
          </template>

          <!-- Everyone else: the classic profile dropdown -->
          <Menu v-else as="div" class="relative inline-block text-left pl-4">
            <div>
              <MenuButton v-slot="{ open }" class="group flex items-center gap-2">
                <span class="relative flex-shrink-0">
                  <img
                    :class="[
                      'w-8 h-8 flex-shrink-0 object-cover rounded-full transition-transform duration-300 group-hover:scale-105',
                      impersonating ? 'border-2 border-orange-500' : null,
                    ]"
                    :src="imageURL" />
                  <BetaBadge v-if="user.is_beta_tester" class="ring-gray-950" />
                </span>
                <img
                  src="../../assets/icons/dropdown.svg"
                  class="w-3 h-3 flex-shrink-0 transition-transform duration-300"
                  :class="open ? 'rotate-180' : ''"
                />
              </MenuButton>
            </div>
            <transition
              enter-active-class="transition ease-out duration-100"
              enter-from-class="transform opacity-0 scale-95"
              enter-to-class="transform opacity-100 scale-100"
              leave-active-class="transition ease-in duration-75"
              leave-from-class="transform opacity-100 scale-100"
              leave-to-class="transform opacity-0 scale-95"
            >
              <MenuItems
                :class="user.is_beta_tester ? 'w-56' : 'w-36'"
                class="absolute right-0 z-10 mt-8 origin-top-right border border-gray-700 shadow-lg divide-y divide-gray-700 rounded-md bg-gray-900 shadow-lg"
                role="menu"
                aria-orientation="vertical"
                aria-labelledby="menu-button"
              >
                <div class="py-1">
                  <div class="px-4 py-3" role="none">
                    <p class="text-sm text-text-secondary" role="none">{{ $t('layout.navbar.signed_in_as') }}</p>
                    <p class="truncate text-sm font-medium text-text-primary" role="none">
                      {{ username }}
                    </p>

                    <button
                      @click="startImpersonating"
                      v-if="canImpersonate"
                      class="text-warning-500 text-xs hover:text-warning-400">
                      {{ $t('layout.nav.impersonate') }}
                    </button>

                    <button
                      v-if="impersonating"
                      @click="stopImpersonating"
                      class="text-warning-500 text-xs hover:text-warning-400"
                    >
                      {{ $t('layout.nav.stop_impersonating') }}
                    </button>
                  </div>
                  <BetaAccessRow v-if="user.is_beta_tester" class="border-y border-gray-700" />
                  <div class="py-1" role="none">
                    <router-link
                      :to="`/user/${getUserLink}`"
                      class="text-text-secondary block px-4 py-2 text-sm hover:bg-gray-700"
                      role="menuitem"
                      tabindex="-1"
                      id="menu-item-0"
                    >{{ $t('layout.nav.my_profile') }}</router-link
                    >
                    <router-link
                      v-if="user.access_level > 2"
                      to="/team"
                      class="text-text-secondary block px-4 py-2 text-sm hover:bg-gray-700"
                      role="menuitem"
                      tabindex="-1"
                      id="menu-item-1"
                    >{{ $t('layout.nav.my_team') }}</router-link
                    >
                    <router-link
                      to="/settings"
                      class="text-text-secondary block px-4 py-2 text-sm hover:bg-gray-700"
                      role="menuitem"
                      tabindex="-1"
                      id="menu-item-2"
                    >{{ $t('common.settings') }}</router-link
                    >
                  </div>
                  <div class="py-1" role="none">
                    <button
                      @click="logout"
                      class="text-danger-500 block w-full px-4 py-2 text-left text-sm hover:bg-gray-700"
                      role="menuitem"
                      tabindex="-1"
                      id="menu-item-3"
                    >
                      {{ $t('layout.nav.sign_out') }}
                    </button>
                  </div>
                </div>
              </MenuItems>
            </transition>
          </Menu>
        </div>
      </div>
    </div>

    <div class="flex md:hidden bg-gray-900 h-[52px]">
      <div class="flex p-4 w-full items-center justify-center">
        <img
          @click="toggleMobileMenu"
          src="../../assets/icons/hamburger.svg"
          class="w-5 h-5 m-auto cursor-pointer"
        />
        <router-link to="/">
          <img :src="$logo" class="ml-3 h-5" />
        </router-link>
        <div class="flex-1" />

        <div class="flex pr-3" v-if="canStartServer()">
          <div
            class="flex w-full justify-end"
            v-if="!activeServer?.id || activeServer.status !== 'online'"
          >
            <router-link to="/play" class="text-[12px] px-4 font-medium flex cursor-pointer items-center justify-center gap-1.5 rounded-md bg-danger-700 duration-200 h-8 text-left text-text-primary hover:bg-danger-600 focus:outline-none focus:ring-0">
              {{ $t('layout.navbar.play_now') }} <img src="../../assets/logos/refrag-icon.svg" class="w-3 h-3" />
            </router-link>
          </div>
          <div
            class="inline-flex h-9 rounded-md items-center px-3 h-8 border select-none border-transparent text-sm font-medium text-white bg-primary-600 hover:bg-primary-500"
            v-else-if="activeServer.status && activeServer?.status !== 'online'"
          >
            <svg class="w-6 h-6 text-white animate-spin" fill="none" viewBox="0 0 24 24">
              <circle
                class="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                stroke-width="4"
              />
              <path
                class="opacity-75"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                fill="currentColor"
              />
            </svg>
          </div>
          <div v-else>
            <router-link
              to="/server_manager"
              type="button"
              class="inline-flex rounded-md items-center px-3 h-8 border select-none border-transparent text-sm text-white bg-primary-600 hover:bg-primary-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-800 focus:ring-indigo-500"
            >
              {{ $t('layout.nav.manage') }}
            </router-link>
          </div>
        </div>

        <div class="flex flex-1 justify-end pr-3" v-else>
          <div v-if="serverLauncherDisabled" class="has-tooltip flex flex-col">
            <button
              type="button"
              class="text-white bg-gray-300 font-medium text-sm px-5 h-8 text-center"
            >
              {{ $t('layout.navbar.under_maintenance') }}
            </button>
            <span
              href="https://discord.gg/Refrag"
              class="tooltip rounded shadow-md p-1 bg-gray-900 text-white mr-2 mt-16"
            >{{ $t('layout.navbar.servers_under_maintenance') }}</span
            >
          </div>
          <div v-else class="has-tooltip flex flex-col">
            <button
              @click="$emit('openUpgradeModal')"
              type="button"
              class="text-white bg-primary-600 hover:bg-primary-500 duration-300 font-medium text-sm px-5 h-8 text-center rounded-md whitespace-nowrap"
            >
              {{ $t('layout.navbar.play_now') }}
            </button>
          </div>
        </div>

        <Menu as="div" class="relative inline-block text-left">
          <div>
            <MenuButton class="group flex items-center">
              <span class="relative flex-shrink-0">
                <img
                  class="w-8 h-8 flex-shrink-0 object-cover rounded-full transition-transform duration-300 group-hover:scale-105"
                  :src="imageURL"
                />
                <BetaBadge v-if="user.is_beta_tester" class="ring-gray-900" />
              </span>
            </MenuButton>
          </div>
          <transition
            enter-active-class="transition ease-out duration-100"
            enter-from-class="transform opacity-0 scale-95"
            enter-to-class="transform opacity-100 scale-100"
            leave-active-class="transition ease-in duration-75"
            leave-from-class="transform opacity-100 scale-100"
            leave-to-class="transform opacity-0 scale-95"
          >
            <MenuItems
              :class="user.is_beta_tester ? 'w-56' : 'w-36'"
              class="absolute right-0 z-10 mt-2 origin-top-right border border-gray-700 shadow-lg divide-y divide-gray-700 rounded-md bg-gray-900 shadow-lg"
              role="menu"
              aria-orientation="vertical"
              aria-labelledby="menu-button"
            >
              <div class="py-1">
                <div class="px-4 py-3" role="none">
                  <p class="text-sm text-text-secondary" role="none">{{ $t('layout.navbar.signed_in_as') }}</p>
                  <p class="truncate text-sm font-medium text-text-primary" role="none">
                    {{ username }}
                  </p>
                </div>
                <BetaAccessRow v-if="user.is_beta_tester" class="border-y border-gray-700" />
                <div class="py-1" role="none">
                  <!-- Active: "bg-gray-100 text-gray-900", Not Active: "text-gray-700" -->
                  <router-link
                    :to="`/user/${getUserLink}`"
                    class="text-text-secondary block px-4 py-2 text-sm hover:bg-gray-700"
                    role="menuitem"
                    tabindex="-1"
                    id="menu-item-0"
                  >{{ $t('layout.nav.my_profile') }}</router-link
                  >
                  <router-link
                    v-if="user.access_level > 2"
                    to="/team"
                    class="text-text-secondary block px-4 py-2 text-sm hover:bg-gray-700"
                    role="menuitem"
                    tabindex="-1"
                    id="menu-item-1"
                  >{{ $t('layout.nav.my_team') }}</router-link
                  >
                  <router-link
                    to="/settings"
                    class="text-text-secondary block px-4 py-2 text-sm hover:bg-gray-700"
                    role="menuitem"
                    tabindex="-1"
                    id="menu-item-2"
                  >{{ $t('common.settings') }}</router-link
                  >
                </div>
                <div class="py-1" role="none">
                  <button
                    @click="logout"
                    class="text-danger-500 block w-full px-4 py-2 text-left text-sm hover:bg-gray-700"
                    role="menuitem"
                    tabindex="-1"
                    id="menu-item-3"
                  >
                    {{ $t('layout.nav.sign_out') }}
                  </button>
                </div>
              </div>
            </MenuItems>
          </transition>
        </Menu>
      </div>
    </div>

    <StartServerModal
      @closeModal="closeModal"
      :open="state.modalOpen"
      :canStartCs2Server="launchCs2Server"
      :canStartCsgoServer="launchCsgoServer"
      :redirect="true"
      v-if="state.modalOpen"
    />
  </div>
  <NotificationsPopup
    @toggleNotifications="toggleNotifications"
    v-bind:showNotifications="showNotifications"
    :notifications="state.notifications"
    @see-notification="seeNotification"
    @clear-all-notifications="clearAllNotifications"
    :toggleNotifications="toggleNotifications"
  />
  <div
    class="mt-4 -mb-4 mx-8 text-white p-1 border-2 border-red-500"
    v-if="globalNotification"
  >
    <p class="m-auto text-center text-sm font-md">
      {{ $t('layout.navbar.global_notification_placeholder') }}
    </p>
  </div>
</template>

<script>
import {
  Menu,
  MenuItem,
  MenuButton,
  MenuItems,
  Switch,
  Popover,
  PopoverButton,
  PopoverPanel,
  TabGroup,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
} from '@headlessui/vue';
import { reactive } from 'vue';
import { mapState, mapMutations } from 'vuex';
import { BellIcon, ClockIcon, StarIcon } from '@heroicons/vue/24/outline';
import {
  ChevronDownIcon, ChevronUpIcon, XMarkIcon, CheckCircleIcon,
} from '@heroicons/vue/20/solid';
import axios from 'axios';
import moment from 'moment';
import ServerInfo from '../ServerInfo.vue';
import NotificationsPopup from '../NotificationsPopup.vue';
import StartServerModal from '../StartServerModal.vue';
import { clearSignedOutUser, confirmSignOut } from '../../utils/session';
import refragIcon from '../../assets/logos/refrag-avatar.svg';
import Streaks from './Streaks/Streaks.vue';
import ProfileSidebar from './ProfileSidebar.vue';
import QueueSearchPill from '../Matchmaking/QueueSearchPill.vue';
import LanOnPill from '../LanParty/LanOnPill.vue';
import BetaBadge from './BetaBadge.vue';
import BetaAccessRow from './BetaAccessRow.vue';

export default {
  setup() {
    const state = reactive({
      modalOpen: false,
      notifications: [],
      siteSettings: [],
      betaServer: false,
      scrimServer: false,
      serverLoading: false,
      navigateServers: false,
      navigateMods: false,
      navigateMaps: false,
      copySucceeded: false,
      terminateSucceeded: false,
      isTerminating: false,
    });

    function toggleModal() {
      state.modalOpen = !state.modalOpen;
    }

    function closeModal() {
      state.modalOpen = false;
    }

    return {
      toggleModal,
      state,
      closeModal,
    };
  },
  data() {
    return {
      showNotifications: false,
      profileSidebarOpen: false,
      globalNotification: false,
      refragIcon,
      websocket: null,
      subscription: null,
      launchSettings: {},
    };
  },
  props: ['user', 'toggleMobileMenu', 'pinProfileSidebar'],
  emits: ['openUpgradeModal'],
  components: {
    StartServerModal,
    ServerInfo,
    BellIcon,
    NotificationsPopup,
    StarIcon,
    ClockIcon,
    Menu,
    MenuItem,
    MenuButton,
    MenuItems,
    Switch,
    Popover,
    PopoverButton,
    PopoverPanel,
    TabGroup,
    TabList,
    Tab,
    TabPanels,
    TabPanel,
    ChevronDownIcon,
    ChevronUpIcon,
    Streaks,
    ProfileSidebar,
    QueueSearchPill,
    LanOnPill,
    BetaBadge,
    BetaAccessRow,
    XMarkIcon,
    CheckCircleIcon,
  },
  computed: {
    activeServer() {
      return this.$store.state.activeServer;
    },
    activeServers() {
      return this.$store.state.activeServers;
    },
    getUserLink() {
      if (this.user.username) {
        return this.user.username;
      }
      return this.user.uuid;
    },
    username() {
      if (this.user.username) {
        return this.user.username;
      } if (this.user.steam_profile) {
        return this.user.steam_profile.personaname;
      }
      return this.user.email;
    },
    imageURL() {
      return this.user.steam_profile?.avatarfull || this.user.twitch?.avatar_url || this.refragIcon;
    },
    mods() {
      return this.$store.state.modInfo?.mods;
    },
    launchCsgoServer() {
      const start_csgo_server = this.state.siteSettings.find(
        (setting) => setting.name === 'start_csgo_server',
      )?.enabled;
      if (typeof start_csgo_server !== 'undefined') return start_csgo_server;
      return true;
    },
    launchCs2Server() {
      const start_cs2_server = this.state.siteSettings.find(
        (setting) => setting.name === 'start_cs2_server',
      )?.enabled;
      if (typeof start_cs2_server !== 'undefined') return start_cs2_server;
      return true;
    },
    serverIP() {
      if (!this.activeServer) return null;
      return this.activeServer.ip;
    },
    serverRawIP() {
      if (!this.activeServer) return null;
      return this.activeServer.raw_ip;
    },
    serverPort() {
      if (!this.activeServer) return null;
      return this.activeServer.port;
    },
    serverPassword() {
      if (!this.activeServer) return null;
      return this.activeServer.password;
    },
    serverID() {
      if (!this.activeServer) return null;
      return this.activeServer.id;
    },
    isAssessment() {
      if (!this.activeServer) return null;
      return this.activeServer.is_assessment;
    },
    activeTeamServer() {
      if (!this.activeServer) return null;
      return this.activeServer;
    },
    serverLauncherDisabled() {
      if (!this.launchCs2Server && !this.launchCsgoServer) {
        return true;
      }
    },
    canImpersonate() {
      return !this.$auth.impersonating() && this.user.can_impersonate;
    },
    canUseProfileSidebar() {
      return !!this.user.can_use_matchmaking;
    },
    impersonating() {
      return this.$auth.impersonating();
    },
    ...mapState(['activeTeam']),
  },
  mounted() {
    this.getCsServer();
    this.getNotifications();
    this.loadSiteSettings();
  },
  unmounted() {
    if (this.subscription) {
      this.subscription.unsubscribe();
      this.websocket.disconnect();
      this.subscription = null;
    }
  },
  methods: {
    ...mapMutations(['updateActiveTeamData', 'showMessage', 'hideMessage', 'setActiveServer']),
    subscribeToServer() {
      if (this.subscription) return;

      console.log('subscribing to server via navbar');

      this.websocket = this.$cable.connect();
      this.subscription = this.websocket.subscriptions.create(
        { channel: 'CsServerChannel', team_id: this.activeTeam.id },
        {
          received: (data) => {
            console.log('received data', data);
            this.$store.state.activeServers = data.servers.filter((server) => server.status === 'online');
            this.$store.state.activeServer = data.servers.find((server) => server.started_by.id === this.user.id && server.status !== 'terminated') || null;
          },
        },
      );
    },
    canStartServer() {
      if (!this.user.teams) return false;
      if (this.serverLauncherDisabled) return false;
      const activeTeam = this.user.teams.find(
        (team) => team.id === this.$store.state.activeTeam?.id,
      );
      return this.user.access_level > 0 && activeTeam && activeTeam.can_start_server;
    },
    finishTerminate() {
      setTimeout(() => {
        this.state.terminateSucceeded = true;
        this.state.isTerminating = false;
        setTimeout(() => {
          this.state.terminateSucceeded = false;
        }, 2000);
      }, 5000);
    },
    toggleTerminate() {
      if (this.isTerminating || this.state.terminateSucceeded) return;
      if (confirm(this.$t('layout.navbar.confirm_terminate_server'))) {
        this.state.isTerminating = true;
        axios({
          url: `/cs_servers/${this.serverID}`,
          method: 'DELETE',
          headers: {
            'X-TEAM-ID': this.activeTeam.id,
          },
        })
          .then(() => {
            this.finishTerminate();
          })
          .catch(() => {
            this.state.isTerminating = false;
            this.showMessage({
              messageType: 'error',
              messageText: this.$t('layout.navbar.terminate_server_failed'),
            });
          });
      }
    },
    openServerSelector() {
      this.state.navigateServers = true;
    },
    toggleNotifications() {
      this.showNotifications = !this.showNotifications;
    },
    loadSiteSettings() {
      axios.get('settings').then(({ data }) => {
        this.state.siteSettings = data.settings;
        this.$store.state.siteSettings = data.settings;
      });
    },
    getCsServer() {
      axios({
        url: '/cs_servers/running',
        method: 'GET',
        headers: {
          'X-TEAM-ID': this.activeTeam.id,
        },
      }).then((response) => {
        if (response.data?.length) {
          this.$store.state.activeServer = response.data.find((server) => server.started_by.id === this.user.id);
          this.$store.state.activeServers = response.data.filter((server) => server.status === 'online');
          this.subscribeToServer();
        } else {
          this.$store.state.activeServer = null;
          this.$store.state.activeServers = null;
          this.subscribeToServer();
        }
      });
    },
    getNotifications() {
      axios.get('/notifications?level=0').then((response) => {
        this.state.notifications = response.data;
      });
    },
    seeNotification(notification) {
      axios.post(`/notifications/${notification.id}/see`).then((response) => {
        this.state.notifications = response.data;
      });
    },
    clearAllNotifications() {
      axios.post('/notifications/see_all').then((response) => {
        this.state.notifications = response.data;
        this.toggleNotifications();
      });
    },
    logout() {
      if (!confirmSignOut(this.username)) return;

      clearSignedOutUser();
      this.$auth.logout({
        makeRequest: true,
        redirect: { name: 'Login' },
      });
    },
    startImpersonating() {
      const userId = prompt(this.$t('layout.navbar.impersonate_prompt'));
      if (userId) {
        this.$auth.impersonate({
          data: {
            id: userId,
          },
          redirect: '/',
          makeRequest: true,
        });
      }
    },
    stopImpersonating() {
      this.$auth.unimpersonate({
        data: {
          id: this.user.id,
          client: this.$auth.token().split('|')[2],
        },
        redirect: '/',
        makeRequest: true,
      });
    },
    copyToClipboard(server) {
      const textToCopy = `connect ${server.ip}:${server.port}; password ${server.password}`;
      navigator.clipboard.writeText(textToCopy)
        .then(() => {
          this.state.copySucceeded = server.id;
          console.log('Text copied to clipboard');
          setTimeout(() => {
            this.state.copySucceeded = null;
          }, 1200);
        })
        .catch((err) => {
          console.error('Failed to copy: ', err);
        });
    },
  },
  watch: {
    activeServer: {
      handler(val) {
        if (val) {
          this.subscribeToServer();
        }
      },
      deep: true,
    },
  },
};
</script>

<style>
.boxshadow-b {
  box-shadow: 0px 1px 5px 0px #0b0e13;
}
</style>
